/**
 * The playful paper layer shared with mobile (mobile/src/theme/tokens.ts,
 * mobile/src/components/fun/marks-geometry.ts, mobile/src/features/home/desk-copy.ts).
 * Pure functions only — the components in `components/paper` render them.
 */

export interface Highlighter {
  fill: string;
  ink: string;
}

/** Stationery highlighters — soft fill + same-hue ink (CSS vars from index.css). */
export const HIGHLIGHTERS = {
  yellow: { fill: 'var(--hl-yellow)', ink: 'var(--hl-yellow-ink)' },
  mint: { fill: 'var(--hl-mint)', ink: 'var(--hl-mint-ink)' },
  coral: { fill: 'var(--hl-coral)', ink: 'var(--hl-coral-ink)' },
  lilac: { fill: 'var(--hl-lilac)', ink: 'var(--hl-lilac-ink)' },
  sky: { fill: 'var(--hl-sky)', ink: 'var(--hl-sky-ink)' },
  pink: { fill: 'var(--hl-pink)', ink: 'var(--hl-pink-ink)' },
} as const satisfies Record<string, Highlighter>;

// Same order as mobile, so a tag gets the same colour on both.
const HIGHLIGHTER_LIST: readonly Highlighter[] = Object.values(HIGHLIGHTERS);

/** Stable highlighter for a label (e.g. a tag): same text → same colour, always. */
export function highlighterFor(label: string): Highlighter {
  const key = label.trim().toLowerCase();
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) | 0;
  return HIGHLIGHTER_LIST[Math.abs(h) % HIGHLIGHTER_LIST.length];
}

/** A stable, subtle tilt (−0.8° … 0.8°) per card, so notes look dropped on a desk. */
export function tiltFor(id: number): number {
  return (((Math.abs(id) * 37) % 5) - 2) * 0.4;
}

export interface Point {
  x: number;
  y: number;
}

/** Deterministic PRNG (same as mobile) so a mark always looks the same. */
export function seeded(seed: number): () => number {
  let a = seed >>> 0 || 1;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** A marker swipe under a word: gentle wave with a slight upward slant. */
export function underlinePoints(width: number, height: number, seed: number): Point[] {
  const rnd = seeded(seed);
  const steps = 24;
  const mid = height / 2;
  const amp = height * 0.22;
  const phase = rnd() * Math.PI * 2;
  const slant = height * (0.15 + rnd() * 0.15);
  const pts: Point[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const x = 2 + t * (width - 4);
    const y = mid + slant * (0.5 - t) + Math.sin(phase + t * Math.PI * 1.6) * amp;
    pts.push({ x, y: clamp(y, 0, height) });
  }
  return pts;
}

/** A loose pen circle (≈1.12 turns, drifting inward) so the ends overlap. */
export function loopPoints(width: number, height: number, seed: number): Point[] {
  const rnd = seeded(seed);
  const cx = width / 2;
  const cy = height / 2;
  const rx = width / 2 - 3;
  const ry = height / 2 - 3;
  const start = -Math.PI * (0.6 + rnd() * 0.3);
  const turns = 1.12;
  const steps = 48;
  const wobblePhase = rnd() * Math.PI * 2;
  const pts: Point[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const a = start + t * turns * Math.PI * 2;
    const drift = 1 - t * 0.06;
    const wobble = 1 + Math.sin(wobblePhase + a * 3) * 0.03;
    pts.push({ x: cx + Math.cos(a) * rx * drift * wobble, y: cy + Math.sin(a) * ry * drift * wobble });
  }
  return pts;
}

/** A line of "handwriting": a squiggle whose loops vary with the seed. */
export function scribblePoints(width: number, height: number, seed: number): Point[] {
  const rnd = seeded(seed);
  const steps = Math.max(8, Math.round(width / 3));
  const mid = height / 2;
  const pts: Point[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const amp = (height / 2 - 1) * (0.45 + rnd() * 0.55);
    const y = mid + Math.sin(t * Math.PI * (width / 7)) * amp;
    pts.push({ x: 1 + t * (width - 2), y: clamp(y, 0, height) });
  }
  return pts;
}

const r1 = (n: number): number => Math.round(n * 10) / 10;

/** Smooth a polyline through its midpoints into an SVG path — one pen move. */
export function toPathD(points: readonly Point[]): string {
  if (points.length < 2) return '';
  let d = `M${r1(points[0].x)} ${r1(points[0].y)}`;
  for (let i = 1; i < points.length - 1; i += 1) {
    const a = points[i];
    const b = points[i + 1];
    d += ` Q${r1(a.x)} ${r1(a.y)} ${r1((a.x + b.x) / 2)} ${r1((a.y + b.y) / 2)}`;
  }
  const last = points[points.length - 1];
  return `${d} L${r1(last.x)} ${r1(last.y)}`;
}

/**
 * Session-wide record of what has already made its entrance, so cards don't
 * replay their rise-in on refetch or remount (mirrors mobile's appear-once).
 */
const seen = new Set<string>();

export function claimFirstAppearance(key: string): boolean {
  const first = !seen.has(key);
  seen.add(key);
  return first;
}
